import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { SquarePen } from 'lucide-react';
import { useMutation } from '@apollo/client/react';

import { useAuth } from 'src/context/auth/use-auth';
import { ConfirmEmailChangeDocument } from 'src/graphql/actions/profile/confirmEmailChange.mutation';
import { ProfileDocument } from 'src/graphql/actions/profile/profile.query';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogClose,
  DialogFooter,
} from '@/components/ui/dialog';

type Props = {
  email: string;
};

const emailFormSchema = z
  .object({
    oldEmail: z.string().email(),
    email: z.string().email(),
    confirmEmail: z.string().email(),
  })
  .refine((data) => data.email !== data.oldEmail, {
    message: 'Email has not changed',
    path: ['email'],
  })
  .refine((data) => data.email === data.confirmEmail, {
    message: 'Emails do not match',
    path: ['confirmEmail'],
  });

const codeFormSchema = z.object({
  code: z
    .string()
    .min(6, {
      message: 'Invalid confirmation code',
    })
    .max(6, {
      message: 'Invalid confirmation code',
    }),
});

const CodeForm = ({ successCallback }: { successCallback: () => void }) => {
  const [confirmEmailChange, { loading }] = useMutation(ConfirmEmailChangeDocument, {
    refetchQueries: [ProfileDocument],
  });

  const codeForm = useForm<z.infer<typeof codeFormSchema>>({
    resolver: zodResolver(codeFormSchema),
    defaultValues: {
      code: '',
    },
    mode: 'onChange',
  });

  const onSubmitCode = async ({ code = '' }) => {
    try {
      const { data } = await confirmEmailChange({ variables: { code } });

      if (data?.confirmEmailChange?.success) {
        successCallback();
        toast.success('Email updated!', {
          description: "We've updated your email address and sign-in details.",
        });
      } else {
        throw new Error(data?.confirmEmailChange?.errors?.[0]);
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        const error = e as Error & { code: string };

        if (error.code === 'CodeMismatchException' || error.code === 'InvalidParameterException') {
          toast.error('Invalid confirmation code, please check your email again!');
        } else {
          toast.error(e.message || 'Something went wrong!');
        }
      }
    }
  };

  return (
    <Form {...codeForm}>
      <form onSubmit={codeForm.handleSubmit(onSubmitCode)}>
        <FormField
          control={codeForm.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirmation code</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter confirmation code" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter className="sm:justify-between mt-6">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button type="submit" isLoading={loading} disabled={!codeForm.formState.isValid || loading}>
            Confirm email
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

const EmailForm = ({ email, successCallback }: { email: string; successCallback: (newEmail: string) => void }) => {
  const { changeEmail } = useAuth();

  const onSubmitEmail = async (dataForm: z.infer<typeof emailFormSchema>) => {
    try {
      await changeEmail(dataForm.email);
      successCallback(dataForm.email);
    } catch (e: unknown) {
      if (e instanceof Error) toast.error(e.message);
    }
  };

  const emailForm = useForm<z.infer<typeof emailFormSchema>>({
    resolver: zodResolver(emailFormSchema),
    values: {
      oldEmail: email,
      email: '',
      confirmEmail: '',
    },
    mode: 'onChange',
  });

  return (
    <Form {...emailForm}>
      <form onSubmit={emailForm.handleSubmit(onSubmitEmail)} className="space-y-4">
        <FormField
          control={emailForm.control}
          name="oldEmail"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Current email</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={emailForm.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>New email</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter new email address" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={emailForm.control}
          name="confirmEmail"
          render={({ field }) => (
            <FormItem className="mb-6">
              <FormLabel>Confirm new email</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter new email address" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter className="sm:justify-between">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button type="submit" isLoading={emailForm.formState.isSubmitting} disabled={!emailForm.formState.isValid}>
            Save changes
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

const EmailDialog = ({ email }: Props) => {
  const [open, setOpen] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const emailSuccessCallback = (newEmail: string) => {
    setIsCodeSent(true);
    setNewEmail(newEmail);
  };

  const codeSuccessCallback = () => {
    setIsCodeSent(false);
    setNewEmail('');
    setOpen(false);
  };

  const onOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      setIsCodeSent(false);
      setNewEmail('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <SquarePen />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] gap-6">
        <DialogHeader className="gap-1.5">
          <DialogTitle className="text-base font-bold">
            {isCodeSent ? 'Confirm email update' : 'Change email'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isCodeSent
              ? `We've sent a confirmation code to ${newEmail}. Enter it below to confirm your new email.`
              : 'Enter your new email address below to update your sign-in details.'}
          </DialogDescription>
        </DialogHeader>
        {isCodeSent ? (
          <CodeForm successCallback={codeSuccessCallback} />
        ) : (
          <EmailForm email={email} successCallback={emailSuccessCallback} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailDialog;
